/**
 * Rotating N³ empty voxel lattice. Hovered slice = one full layer of cubes.
 * No mystery model, no wireframe outline — only the grid cells.
 */
import React, { useEffect, useRef } from 'react';

const AXIS_COLOR = {
  x: '#5ec8ff',
  y: '#9dff7a',
  z: '#ffb86b',
};

const GRID = '#4a6280';

export default function SliceGuideCube({
  size = 16,
  focus = null,
  solved = [],
  offeredCount = 0,
  height = 220,
}) {
  const mountRef = useRef(null);
  const latestRef = useRef({ focus, solved, size });
  latestRef.current = { focus, solved, size };

  useEffect(() => {
    let disposed = false;
    let cleanup = () => {};

    (async () => {
      try {
        const THREE = await import('three');
        if (disposed || !mountRef.current) return;
        cleanup = mountGuide(THREE, mountRef.current, {
          size,
          height,
          ...latestRef.current,
        });
        const api = mountRef.current.__vqGuide;
        if (api) api(latestRef.current.focus, latestRef.current.solved);
      } catch (e) {
        console.warn('[SliceGuide] Three unavailable', e);
        if (disposed || !mountRef.current) return;
        cleanup = mountGuideCanvas(mountRef.current, {
          size,
          height,
          ...latestRef.current,
        });
        const api = mountRef.current.__vqGuide;
        if (api) api(latestRef.current.focus, latestRef.current.solved);
      }
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, [size, height]);

  useEffect(() => {
    const api = mountRef.current?.__vqGuide;
    if (api) api(focus, solved);
  }, [focus, solved]);

  const totalPlanes = size * 3;
  const hint = focus
    ? `${String(focus.axis).toUpperCase()} = ${focus.index} · full ${size}×${size} layer`
    : `Hover a tile · ${offeredCount || '—'} playable of ${totalPlanes} planes on this ${size}³ grid`;

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        ref={mountRef}
        style={{
          width: '100%',
          height,
          borderRadius: 12,
          border: '1px solid rgba(247, 240, 232, 0.08)',
          background: '#2a2420',
          overflow: 'hidden',
        }}
      />
      <div
        style={{
          marginTop: 6,
          fontSize: '0.72rem',
          opacity: 0.75,
          textAlign: 'center',
          letterSpacing: '0.03em',
          fontFamily: "'JetBrains Mono', monospace",
          color: '#a89b91',
        }}
      >
        {hint}
      </div>
    </div>
  );
}

function cellWorld(x, y, z, size) {
  const half = size / 2;
  return {
    x: x + 0.5 - half,
    y: half - (y + 0.5),
    z: z + 0.5 - half,
  };
}

function forEachSliceCell(size, axis, index, fn) {
  for (let a = 0; a < size; a++) {
    for (let b = 0; b < size; b++) {
      let x;
      let y;
      let z;
      if (axis === 'z') {
        z = index;
        y = a;
        x = b;
      } else if (axis === 'y') {
        y = index;
        z = a;
        x = b;
      } else {
        x = index;
        z = a;
        y = b;
      }
      fn(x, y, z);
    }
  }
}

function mountGuide(THREE, mount, opts) {
  const { size, height } = opts;
  const width = mount.clientWidth || 320;
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setClearColor(0x2a2420, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height);
  if (THREE.SRGBColorSpace) renderer.outputColorSpace = THREE.SRGBColorSpace;
  mount.innerHTML = '';
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 200);
  const dist = size * 1.85;
  camera.position.set(dist * 0.8, dist * 0.58, dist * 1.25);
  camera.lookAt(0, 0, 0);

  const pivot = new THREE.Group();
  pivot.rotation.y = 0.55;
  scene.add(pivot);

  const unitGeo = new THREE.BoxGeometry(0.82, 0.82, 0.82);
  const dummy = new THREE.Object3D();
  const capacity = size * size * size;

  // Full N³ lattice — every cell is a dim voxel (no model, no outline)
  const gridMat = new THREE.MeshBasicMaterial({
    color: GRID,
    transparent: true,
    opacity: 0.11,
    depthWrite: false,
    toneMapped: false,
  });
  const gridMesh = new THREE.InstancedMesh(unitGeo, gridMat, capacity);
  {
    let n = 0;
    for (let z = 0; z < size; z++) {
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const p = cellWorld(x, y, z, size);
          dummy.position.set(p.x, p.y, p.z);
          dummy.updateMatrix();
          gridMesh.setMatrixAt(n++, dummy.matrix);
        }
      }
    }
    gridMesh.count = n;
    gridMesh.instanceMatrix.needsUpdate = true;
  }
  pivot.add(gridMesh);

  // Solved planes (quiet)
  const solvedMat = new THREE.MeshBasicMaterial({
    color: '#8899aa',
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    toneMapped: false,
  });
  const solvedMesh = new THREE.InstancedMesh(unitGeo, solvedMat, size * size * 16);
  solvedMesh.count = 0;
  solvedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  pivot.add(solvedMesh);

  // Focus plane — full size×size layer
  const focusMat = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    toneMapped: false,
  });
  const focusMesh = new THREE.InstancedMesh(unitGeo, focusMat, size * size);
  focusMesh.count = 0;
  focusMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  pivot.add(focusMesh);

  const paintPlanes = (focus, solved) => {
    const skip = focus?.axis != null ? `${focus.axis}:${focus.index}` : null;

    let ns = 0;
    for (const s of solved || []) {
      if (s?.axis == null || s?.index == null) continue;
      if (`${s.axis}:${s.index}` === skip) continue;
      const col = AXIS_COLOR[s.axis] || '#8899aa';
      // reuse one material color — last axis wins for mixed; paint per-axis batches simply
      solvedMat.color.set(col);
      forEachSliceCell(size, s.axis, s.index, (x, y, z) => {
        if (ns >= size * size * 16) return;
        const p = cellWorld(x, y, z, size);
        dummy.position.set(p.x, p.y, p.z);
        dummy.updateMatrix();
        solvedMesh.setMatrixAt(ns++, dummy.matrix);
      });
    }
    solvedMesh.count = ns;
    solvedMesh.instanceMatrix.needsUpdate = true;

    if (!focus || focus.axis == null || focus.index == null) {
      focusMesh.count = 0;
      focusMesh.instanceMatrix.needsUpdate = true;
      return;
    }
    focusMat.color.set(AXIS_COLOR[focus.axis] || '#ffffff');
    let nf = 0;
    forEachSliceCell(size, focus.axis, focus.index, (x, y, z) => {
      const p = cellWorld(x, y, z, size);
      dummy.position.set(p.x, p.y, p.z);
      dummy.updateMatrix();
      focusMesh.setMatrixAt(nf++, dummy.matrix);
    });
    focusMesh.count = nf;
    focusMesh.instanceMatrix.needsUpdate = true;
  };

  paintPlanes(opts.focus, opts.solved);
  mount.__vqGuide = paintPlanes;

  let raf = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    pivot.rotation.y += 0.006;
    renderer.render(scene, camera);
  };
  loop();

  return () => {
    cancelAnimationFrame(raf);
    unitGeo.dispose();
    gridMat.dispose();
    solvedMat.dispose();
    focusMat.dispose();
    renderer.dispose();
    mount.innerHTML = '';
    delete mount.__vqGuide;
  };
}

function mountGuideCanvas(mount, opts) {
  const { size, height } = opts;
  const canvas = document.createElement('canvas');
  const width = mount.clientWidth || 320;
  canvas.width = width;
  canvas.height = height;
  mount.innerHTML = '';
  mount.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  let angle = 0.55;
  let focus = opts.focus;
  let solved = opts.solved || [];

  const project = (x, y, z) => {
    const s = Math.min(width, height) / (size * 2.2);
    const cx = width / 2;
    const cy = height / 2 + 4;
    const rx = x * Math.cos(angle) - z * Math.sin(angle);
    const rz = x * Math.sin(angle) + z * Math.cos(angle);
    return [cx + (rx - rz) * s * 0.85, cy + ((rx + rz) * 0.38 - y) * s * 0.85];
  };

  const drawCell = (x, y, z, color, alpha) => {
    const p = cellWorld(x, y, z, size);
    const [sx, sy] = project(p.x, p.y, p.z);
    const sc = Math.min(width, height) / (size * 2.5);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.fillRect(sx - sc * 0.38, sy - sc * 0.38, sc * 0.76, sc * 0.76);
    ctx.globalAlpha = 1;
  };

  const draw = () => {
    ctx.fillStyle = '#101820';
    ctx.fillRect(0, 0, width, height);
    // Full lattice
    for (let z = 0; z < size; z++) {
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          drawCell(x, y, z, GRID, 0.12);
        }
      }
    }
    for (const s of solved) {
      if (s?.axis == null) continue;
      forEachSliceCell(size, s.axis, s.index, (x, y, z) => {
        drawCell(x, y, z, AXIS_COLOR[s.axis] || '#889', 0.35);
      });
    }
    if (focus?.axis != null) {
      forEachSliceCell(size, focus.axis, focus.index, (x, y, z) => {
        drawCell(x, y, z, AXIS_COLOR[focus.axis] || '#fff', 0.9);
      });
    }
  };

  const sync = (f, s) => {
    focus = f;
    solved = s || [];
    draw();
  };
  mount.__vqGuide = sync;

  let raf = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    angle += 0.008;
    draw();
  };
  loop();

  return () => {
    cancelAnimationFrame(raf);
    mount.innerHTML = '';
    delete mount.__vqGuide;
  };
}
